import styles from './template.css';
import flatpickrCss from './flatpickr.css';
import template from './template';
import {AoflElement, customElement, property} from '@aofl/element';
import flatpickr from 'flatpickr';

const TRANSLATION_CONFIG = {
  'default': flatpickr.l10ns.default,
  'zh-CN': {
    key: 'Mandarin',
    resolve: () => import('flatpickr/dist/l10n/zh')
  },
  'zh-TW': {
    key: 'MandarinTraditional',
    resolve: () => import('flatpickr/dist/l10n/zh-tw')
  },
  'ja-JP': {
    key: 'Japanese',
    resolve: () => import('flatpickr/dist/l10n/ja')
  },
  'es-US': {
    key: 'Spanish',
    resolve: () => import('flatpickr/dist/l10n/es')
  },
};

const attrMap = {
  'default-date': 'defaultDate',
  'default-hour': 'defaultHour',
  'default-minute': 'defaultMinute',
  'disable-dates': 'disable',
  'enables-dates': 'enable',
  'enable-time': 'enableTime',
  'enable-seconds': 'enableSeconds',
  'hour-increment': 'hourIncrement',
  'inline': 'inline',
  'max-date': 'maxDate',
  'min-date': 'minDate',
  'minute-increment': 'minuteIncrement',
  'mode': 'mode',
  'no-calendar': 'noCalendar',
  'week-numbers': 'weekNumbers',
};

const converter = (value) => {
  const convert = (v) => {
    if (isNaN(v)) {
      return new Date(v);
    }
    return new Date(Number(v));
  };

  try {
    const dateArr = JSON.parse(value);
    return dateArr.map(convert);
  } catch (e) {
    return convert(value);
  }
};

/**
 * @summary DatePicker
 * @extends {AoflElement}
 */
@customElement('date-picker')
class DatePicker extends AoflElement {
  /**
   * Creates an instance of DatePicker.
   */
  constructor() {
    super();
    this.observer = this.langListener();
  }

  /**
   * @readonly
   */
  static get is() {
    return 'date-picker';
  }
  @property({attribute: false})
  value;
  @property({type: Boolean})
  static = false;
  @property({converter})
  ['default-date'] = null;
  @property({type: Number})
  ['default-hour'] = 12;
  @property({type: Number})
  ['default-minute'] = 0;
  @property({converter})
  ['disable-dates'] = [];
  @property({converter})
  ['enable-dates'] = [];
  @property({type: Boolean})
  ['enable-time'] = false;
  @property({type: Boolean})
  ['enable-seconds'] = false;
  @property({type: Number})
  ['hour-increment'] = 1;
  @property({type: Boolean})
  inline = false;
  @property({converter})
  ['max-date'] = null;
  @property({converter})
  ['min-date'] = null;
  @property({type: Number})
  ['minute-increment'] = 5;
  @property({type: String})
  mode = 'single';
  @property({type: Boolean})
  ['no-calendar'] = false;
  @property({type: Boolean})
  ['week-numbers'] = false;
  @property({type: String})
  lang;
  @property({type: Boolean})
  wrap = false;
  @property({type: Array})
  plugins = []
  l10nOptions = TRANSLATION_CONFIG.default;
  rendered = false;

  /**
     * Listens for html lang mutations
     * @return {MutationObserver}
     */
  langListener() {
    const observer = new MutationObserver((mutationList) => {
      if (this.getAttribute('lang')) { return; }
      this.lang = mutationList[0].target.lang;
    });
    observer.observe(document.documentElement, {attributes: true});
    return observer;
  }

  renderPicker() {
    const config = {
      plugins: this.plugins,
      wrap: this.wrap,
      static: this.static,
      appendTo: this.shadowRoot,
      allowInput: false,
      defaultDate: this['default-date'],
      defaultHour: this['default-hour'],
      defaultMinute: this['default-minute'],
      disable: this['disable-dates'],
      enableTime: this['enable-time'],
      enableSeconds: this['enable-seconds'],
      hourIncrement: this['hour-increment'],
      inline: this.inline,
      maxDate: this['max-date'],
      minDate: this['min-date'],
      minuteIncrement: this['minute-increment'],
      mode: this.mode,
      noCalendar: this['no-calendar'],
      weekNumbers: this['week-numbers'],
      locale: this.l10nOptions,
      onChange: (selectedDates, dateStr) => {
        this.value = selectedDates;
        this.dispatchEvent(new CustomEvent('change', {
          composed: true,
          detail: {selectedDates, dateStr}
        }));
      },
      onClose: (selectedDates, dateStr) => {
        this.dispatchEvent(new CustomEvent('close', {
          composed: true,
          detail: {selectedDates, dateStr}
        }));
      },
      onOpen: (selectedDates, dateStr) => {
        this.dispatchEvent(new CustomEvent('open', {
          composed: true,
          detail: {selectedDates, dateStr}
        }));
      }
    };

    if (this['enable-dates'].length > 0) {
      config.enable = this['enable-dates'];
    } else {
      // WORKAROUND: bug in flatpickr not accepting empty array
      config.enable = [() => true];
    }

    this.picker = flatpickr(this.elm, config);

    if (this.value) {
      this.picker.setDate(this.value);
    }
  }

  async connectedCallback() {
    this.lang = this.getAttribute('lang') || document.documentElement.getAttribute('lang');
    super.connectedCallback();
    await this.updateComplete;

    const slot = this.shadowRoot.querySelector('slot');
    const container = slot?.assignedElements()[0] || this.shadowRoot;

    this.elm = container.querySelector('input');
    if (this.wrap) {
      this.elm = this.elm.parentElement;
    }

    this.renderPicker();
  }

  shouldUpdate(changedProperties) {
    if (changedProperties.has('lang')) {
      if (name === 'lang') {
        if (!this.lang) {
          this.lang = document.documentElement.lang;
        }
      }

      const langTranslation = TRANSLATION_CONFIG[this.lang];
      if (typeof langTranslation !== 'undefined' && typeof langTranslation.resolve === 'function') {
        langTranslation.resolve().then((localeTranslations) => {
          this.l10nOptions = localeTranslations[langTranslation.key];
          if (this.picker) {
            this.picker.set('locale', this.l10nOptions);
            this.picker.destroy();
            this.renderPicker();
          }
        });
      } else {
        this.l10nOptions = TRANSLATION_CONFIG.default;
        if (this.picker) {
          this.picker.set('locale', this.l10nOptions);
          this.picker.destroy();
          this.renderPicker();
        }
      }
    }
    if (this.picker) {
      changedProperties.forEach((item, key) => {
        if (typeof attrMap[key] !== 'undefined') {
          this.picker.set(attrMap[key], this[key]);
        } else if (key === 'value') {
          this.picker.setDate(this[key], true);
        }
      });
      this.picker.redraw();
    }


    return !this.rendered;
  }
  firstUpdated() {
    this.rendered = true;
  }
  /**
   *
   * @return {Object}
   */
  render() {
    return super.render(template, [styles, flatpickrCss]);
  }

  disconnectedCallback() {
    this.observer.disconnect();
    super.disconnectedCallback();
  }
}

export {DatePicker};
